import TimelineIcon from "@/assets/icons/ui/timeline.svg";
import { ExpandableIconButton } from "@/component/ExpandableIconButton";
import { ArtifactsViewerData } from "@/hooks/useArtifactsViewerData";
import { RtcTask } from "@/store/thread";
import { MediaEditorItem, MediaVersion } from "@/types/mediaArtifact";
import { useMemo } from "react";
import { useArtifactsViewerContext } from "../context/ArtifactsViewerContext";
import { buildVideoStoryboardItems } from "../utils/mediaItemBuilders";
import { MediaArtifactItem } from "./MediaArtifact/MediaArtifactItem";
import { Section } from "./Section";

interface VideoStoryboardGridProps {
  id: string;
  visible: boolean;
  title: string;
  canEdit: boolean;
  data: ArtifactsViewerData["videoStoryboard"]["data"];
  rtcTasks: RtcTask[];
  manuallyAddedVersions: Record<number, MediaVersion[]>;
  selectedVersions?: Record<number, string>;
  onItemClick: (item: MediaEditorItem, index: number) => void;
}

// 用于创建稳定的点击处理器
interface ItemWithHandler {
  item: MediaEditorItem;
  onClick: () => void;
}

export function VideoStoryboardGrid(props: VideoStoryboardGridProps) {
  const {
    id,
    data,
    visible,
    title,
    canEdit,
    onItemClick,
    rtcTasks,
    manuallyAddedVersions,
    selectedVersions,
  } = props;

  const { onSwitchToTimeline } = useArtifactsViewerContext();

  const handleTimelineViewClick = () => {
    onSwitchToTimeline?.("video");
  };

  const mediaEditorItems = useMemo(
    () =>
      buildVideoStoryboardItems(
        data,
        rtcTasks,
        manuallyAddedVersions,
        selectedVersions
      ),
    [data, rtcTasks, manuallyAddedVersions, selectedVersions]
  );

  // 创建带稳定 onClick 的列表，避免每次渲染都创建新函数
  const itemsWithHandlers = useMemo<ItemWithHandler[]>(
    () =>
      mediaEditorItems.map((item, index) => ({
        item,
        onClick: () => onItemClick(item, index),
      })),
    [mediaEditorItems, onItemClick]
  );

  if (!visible) {
    return null;
  }

  return (
    <Section
      id={id}
      title={title}
      extra={
        <ExpandableIconButton
          className="ml-1 align-text-bottom"
          icon={<TimelineIcon className="w-6 h-6" />}
          onClick={handleTimelineViewClick}
        >
          View In Timeline
        </ExpandableIconButton>
      }
    >
      <div className={`artifacts-grid grid-cols-3_4`}>
        {itemsWithHandlers.map(({ item, onClick }, index) => (
          <MediaArtifactItem
            key={item.current.id}
            item={item}
            index={index}
            canEdit={canEdit}
            mediaType={item.mediaType}
            onClick={onClick}
            showIndexBadge
          />
        ))}
      </div>
    </Section>
  );
}
